import React, { useState, useEffect, lazy, Suspense } from 'react';
import axios from 'axios';
import api from '../api/api';
import transactionService from '../services/transactionService';
import backendTransactionService from '../services/backendTransactionService';
import AdminPayoutManagement from './AdminPayoutManagement';
import AdminFeeSlabConfig from './AdminFeeSlabConfig';
import AdminRevenueDashboard from './AdminRevenueDashboard';
import AdminPayoutUserManagement from './AdminPayoutUserManagement';
import AdminMerchantManagement from './AdminMerchantManagement';
import AdminBankApiConfig from './AdminBankApiConfig';
import ErrorBoundary from './ErrorBoundary';
import usePagination from '../hooks/usePagination';
import Pagination from './Pagination';

const AdminLedgerDashboard = lazy(() => import('./AdminLedgerDashboard'));

const AdminDashboard = () => {
  const [merchants, setMerchants] = useState([]);
  const [merchantTxStats, setMerchantTxStats] = useState({}); // per-merchant tx stats from backend
  const [merchantStats, setMerchantStats] = useState({
    totalMerchants: 0,
    activeMerchants: 0,
    inactiveMerchants: 0
  });
  const [paymentStats, setPaymentStats] = useState({
    totalPayments: 0,
    successfulPayments: 0,
    totalAmount: 0,
    successRate: 0
  });
  const [payoutStats, setPayoutStats] = useState({
    totalPayouts: 0,
    successfulPayouts: 0,
    failedPayouts: 0,
    pendingPayouts: 0,
    totalVolume: 0,
    successRate: 0
  });
  const [kycStats, setKycStats] = useState({
    totalKycApplications: 0,
    approvedKyc: 0,
    pendingKyc: 0,
    rejectedKyc: 0
  });
  const [selectedMerchant, setSelectedMerchant] = useState(null);
  const [selectedMerchantTransactions, setSelectedMerchantTransactions] = useState(null);
  const [adminTxPage, setAdminTxPage] = useState(1);
  const ADMIN_TX_PAGE_SIZE = 10;
  const [merchantPage, setMerchantPage] = useState(1);
  const MERCHANT_PAGE_SIZE = 10;
  const [merchantStatusFilter, setMerchantStatusFilter] = useState('all'); // 'all', 'active', 'inactive'
  const [merchantSearch, setMerchantSearch] = useState('');
  const [transactionFilters, setTransactionFilters] = useState({
    timePeriod: '',
    status: '',
    paymentMethod: '',
    dateFrom: '',
    dateTo: '',
    minAmount: '',
    maxAmount: '',
    searchTerm: ''
  });
  const [newApiKey, setNewApiKey] = useState('');
  const [loading, setLoading] = useState(true);
  // Amount reconciliation (request vs response amount mismatch) state
  const [reconLoading, setReconLoading] = useState(false);
  const [reconLoaded, setReconLoaded] = useState(false);
  const [reconByMerchant, setReconByMerchant] = useState([]); // [{ merchant, mismatches: [...] }]
  const [reconError, setReconError] = useState('');
  const [activeTab, setActiveTabState] = useState(() => {
    const hash = window.location.hash.replace('#', '');
    const validTabs = ['merchants', 'payouts', 'stats', 'merchantMgmt', 'bankApi', 'payoutUsers', 'fees', 'revenue', 'ledger', 'reconciliation', 'config'];
    return validTabs.includes(hash) ? hash : 'merchants';
  });
  const setActiveTab = (tab) => { window.location.hash = tab; setActiveTabState(tab); };
  const [useBackendData] = useState(true); // Always use backend data

  useEffect(() => {
    fetchMerchants();
    fetchMerchantStats();
    fetchPaymentStats();
    fetchPayoutStats();
  }, []); // Load once on mount

  // Reset to Merchants tab when navbar "Admin Dashboard" is clicked while already on this page
  useEffect(() => {
    const handler = () => setActiveTab('merchants');
    window.addEventListener('resetAdminTab', handler);
    return () => window.removeEventListener('resetAdminTab', handler);
  }, []);

  // Auto-apply filters when transactionFilters change
  useEffect(() => {
    if (selectedMerchantTransactions && selectedMerchantTransactions.transactions && selectedMerchantTransactions.transactions.length > 0) {
      console.log('🔄 Auto-applying filters due to filter change');
      console.log('🔍 Current filters:', transactionFilters);
      console.log('📊 Original transactions count:', selectedMerchantTransactions.transactions.length);
      
      const applyFilters = async () => {
        let filtered;
        
        if (useBackendData) {
          // Use backend service for filtering REAL data
          console.log('📡 Auto-filtering with backend service...');
          filtered = await backendTransactionService.searchTransactions(
            selectedMerchantTransactions.merchantId,
            transactionFilters
          );
        } else {
          // Use localStorage service for demo data
          console.log('💾 Auto-filtering with localStorage service...');
          filtered = transactionService.searchTransactions(
            selectedMerchantTransactions.merchantId,
            transactionFilters
          );
        }
        
        console.log('✅ Auto-filtered transactions count:', filtered.length);
        console.log('📋 Sample filtered transaction:', filtered.length > 0 ? filtered[0] : 'No transactions');
        
        setSelectedMerchantTransactions(prev => ({
          ...prev,
          filteredTransactions: filtered
        }));
      };
      
      applyFilters();
    }
  }, [
    transactionFilters.timePeriod,
    transactionFilters.status,
    transactionFilters.paymentMethod,
    transactionFilters.dateFrom,
    transactionFilters.dateTo,
    transactionFilters.minAmount,
    transactionFilters.maxAmount,
    transactionFilters.searchTerm,
    selectedMerchantTransactions?.merchantId,
    useBackendData
  ]); // Watch individual filter fields to ensure proper triggering

  // Apply filters when transactions are initially loaded or refreshed
  useEffect(() => {
    if (selectedMerchantTransactions && selectedMerchantTransactions.transactions && !selectedMerchantTransactions.filteredTransactions) {
      console.log('🔄 Initial filter application for loaded transactions');
      
      const applyInitialFilters = async () => {
        let filtered;
        
        if (useBackendData) {
          filtered = await backendTransactionService.searchTransactions(
            selectedMerchantTransactions.merchantId,
            transactionFilters
          );
        } else {
          filtered = transactionService.searchTransactions(
            selectedMerchantTransactions.merchantId,
            transactionFilters
          );
        }
        
        setSelectedMerchantTransactions(prev => ({
          ...prev,
          filteredTransactions: filtered
        }));
      };
      
      applyInitialFilters();
    }
  }, [selectedMerchantTransactions?.transactions]); // Only trigger when transactions are loaded

  // Calculate stats from filtered transactions
  const calculateFilteredStats = (transactions) => {
    if (!transactions || transactions.length === 0) {
      return {
        totalTransactions: 0,
        successfulTransactions: 0,
        pendingTransactions: 0,
        failedTransactions: 0,
        refundedTransactions: 0,
        totalAmount: 0,
        totalFees: 0,
        totalRefunds: 0,
        averageTransactionAmount: 0,
        successRate: 0
      };
    }

    const stats = {
      totalTransactions: transactions.length,
      successfulTransactions: transactions.filter(t => t.status === 'SUCCESS').length,
      pendingTransactions: transactions.filter(t => t.status === 'PENDING').length,
      failedTransactions: transactions.filter(t => t.status === 'FAILED').length,
      refundedTransactions: transactions.filter(t => t.status === 'REFUNDED').length,
      totalAmount: transactions.reduce((sum, t) => sum + (t.status === 'SUCCESS' ? t.amount : 0), 0),
      totalFees: transactions.reduce((sum, t) => sum + (t.status === 'SUCCESS' ? (t.fees || 0) : 0), 0),
      totalRefunds: transactions.reduce((sum, t) => sum + (t.refundAmount || 0), 0),
      averageTransactionAmount: 0,
      successRate: 0
    };

    if (stats.totalTransactions > 0) {
      stats.averageTransactionAmount = stats.successfulTransactions > 0 
        ? Math.round(stats.totalAmount / stats.successfulTransactions) 
        : 0;
      stats.successRate = Math.round((stats.successfulTransactions / stats.totalTransactions) * 100);
    }

    return stats;
  };

  const fetchMerchants = async () => {
    try {
      const response = await axios.get('/api/merchant/config/admin/all');
      if (response.data.success) {
        setMerchants(response.data.data);
        // Fetch per-merchant transaction stats from backend
        fetchPerMerchantStats(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching merchants:', error);
      if (error.response?.status === 401) {
        window.location.href = '/admin/login';
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchPerMerchantStats = async (merchantList) => {
    try {
      const res = await axios.get('/api/payment/admin/merchant-stats-batch');
      if (res.data.success) {
        const batchData = res.data.data || {};
        const statsMap = {};
        for (const m of merchantList) {
          statsMap[m.id] = batchData[m.id] || { totalTransactions: 0, successRate: 0 };
        }
        setMerchantTxStats(statsMap);
      }
    } catch (e) {
      console.error('Error fetching per-merchant stats:', e);
    }
  };

  const fetchMerchantStats = async () => {
    try {
      const response = await axios.get('/api/merchant/config/admin/stats');
      if (response.data.success) {
        setMerchantStats(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching merchant stats:', error);
      if (error.response?.status === 401) {
        window.location.href = '/admin/login';
      }
    }
  };

  const fetchPaymentStats = async () => {
    try {
      const response = await axios.get('/api/payment/admin/stats');
      if (response.data.success) {
        const d = response.data.data;
        setPaymentStats({
          totalPayments: d.totalPayments || 0,
          successfulPayments: d.successfulPayments || 0,
          totalAmount: d.totalAmount || 0,
          successRate: d.successRate || 0
        });
      }
    } catch (error) {
      console.error('Error fetching payment stats:', error);
      if (error.response?.status === 401) {
        window.location.href = '/admin/login';
      }
    }
  };

  const fetchPayoutStats = async () => {
    try {
      const response = await axios.get('/api/v1/admin/payouts/stats');
      if (response.data.success) {
        setPayoutStats(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching payout stats:', error);
    }
  };

  const generateApiKey = async (merchantId) => {
    try {
      const response = await axios.post(`/api/admin/merchants/${merchantId}/generate-api-key`, {});
      if (response.data.success) {
        setNewApiKey(response.data.data.apiKey);
        fetchMerchants();
      }
    } catch (error) {
      console.error('Error generating API key:', error);
      alert('Failed to generate API key');
      if (error.response?.status === 401) {
        window.location.href = '/admin/login';
      }
    }
  };

  const toggleMerchantStatus = async (merchantId, currentStatus) => {
    try {
      const response = await axios.put(`/api/admin/merchants/${merchantId}/status`, {
        isActive: !currentStatus
      });
      if (response.data.success) {
        fetchMerchants();
      }
    } catch (error) {
      console.error('Error updating merchant status:', error);
      alert('Failed to update merchant status');
      if (error.response?.status === 401) {
        window.location.href = '/admin/login';
      }
    }
  };

  const viewMerchantTransactions = async (merchantId, forceRefresh = false) => {
    try {
      console.log('🔍 Viewing transactions for merchant:', merchantId, 'using backend:', useBackendData, 'forceRefresh:', forceRefresh);
      
      let transactions, stats;
      
      if (useBackendData) {
        // ALWAYS use backend transaction service - NO FALLBACK to demo data
        console.log('📡 Fetching REAL transactions from backend API...');
        
        if (forceRefresh) {
          // Force refresh - bypass cache
          console.log('🔄 Force refreshing data...');
          backendTransactionService.clearMerchantCache(merchantId);
          transactions = await backendTransactionService.forceRefreshMerchantTransactions(merchantId);
        } else {
          transactions = await backendTransactionService.getMerchantTransactions(merchantId);
        }
        
        stats = await backendTransactionService.getMerchantTransactionStats(merchantId);
        
        console.log('📊 Backend returned transactions:', transactions.length);
        console.log('📊 Backend stats:', stats);
        console.log('📊 Sample transaction data:', transactions.length > 0 ? transactions[0] : 'No transactions');
        
        // DO NOT fall back to demo data - show actual backend state
        if (transactions.length === 0) {
          console.log('ℹ️ No real transactions found for merchant:', merchantId);
          console.log('ℹ️ This means the merchant has not created any payments yet');
        }
      } else {
        // Only use demo data when explicitly requested
        transactions = transactionService.getMerchantTransactions(merchantId);
        stats = transactionService.getMerchantTransactionStats(merchantId);
        console.log('📊 Demo transactions loaded:', transactions.length);
      }
      
      console.log('📊 Final transactions to display:', transactions.length, 'stats:', stats);
      
      setAdminTxPage(1);
      setSelectedMerchantTransactions({
        merchantId,
        transactions,
        stats,
        filteredTransactions: transactions
      });
    } catch (error) {
      console.error('❌ Error viewing merchant transactions:', error);
      console.error('❌ Error details:', error.response?.data);
      
      // Show error state instead of falling back to demo data
      setSelectedMerchantTransactions({
        merchantId,
        transactions: [],
        stats: {
          totalTransactions: 0,
          successfulTransactions: 0,
          pendingTransactions: 0,
          failedTransactions: 0,
          refundedTransactions: 0,
          totalAmount: 0,
          totalFees: 0,
          totalRefunds: 0,
          averageTransactionAmount: 0,
          successRate: 0
        },
        filteredTransactions: [],
        error: error.message
      });
    }
  };



  const clearTransactionFilters = () => {
    setTransactionFilters({
      timePeriod: '',
      status: '',
      paymentMethod: '',
      dateFrom: '',
      dateTo: '',
      minAmount: '',
      maxAmount: '',
      searchTerm: ''
    });
    
    if (selectedMerchantTransactions) {
      setSelectedMerchantTransactions(prev => ({
        ...prev,
        filteredTransactions: prev.transactions
      }));
    }
  };

  const clearAllTransactionData = () => {
    if (window.confirm('Are you sure you want to clear all transaction data for all merchants? This action cannot be undone.')) {
      try {
        transactionService.clearAllTransactionData();
        
        // Refresh stats
        fetchMerchantStats();
        fetchPaymentStats();
        
        alert('All transaction data has been cleared successfully.');
      } catch (error) {
        console.error('Error clearing transaction data:', error);
        alert('Failed to clear transaction data.');
      }
    }
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  // Render a naive UTC backend timestamp as IST (+5:30). DB stores UTC; UI shows IST.
  const formatIstDateTime = (value) => {
    if (!value) return '';
    const m = String(value).match(/(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2}):(\d{2})/);
    if (!m) return String(value);
    const [, yyyy, mm, dd, HH, MI, SS] = m;
    const utcMs = Date.UTC(+yyyy, +mm - 1, +dd, +HH, +MI, +SS);
    const ist = new Date(utcMs + (5 * 60 + 30) * 60 * 1000);
    const p = (n) => String(n).padStart(2, '0');
    let h = ist.getUTCHours();
    const ampm = h >= 12 ? 'pm' : 'am';
    h = h % 12 || 12;
    return `${p(ist.getUTCDate())}/${p(ist.getUTCMonth() + 1)}/${ist.getUTCFullYear()}, ${p(h)}:${p(ist.getUTCMinutes())} ${ampm}`;
  };

  // A transaction is a "mismatch" when the bank responded with a paid amount
  // (paidAmount present) that differs from the request/intent amount.
  const isAmountMismatch = (t) => {
    if (t.paidAmount === null || t.paidAmount === undefined) return false;
    const req = parseFloat(t.amount);
    const res = parseFloat(t.paidAmount);
    if (isNaN(req) || isNaN(res)) return false;
    // Compare in paise to avoid floating point noise.
    return Math.round(req * 100) !== Math.round(res * 100);
  };

  // Load every merchant's transactions and keep only amount mismatches,
  // grouped merchant-wise, for the reconciliation tab.
  const loadReconciliation = async () => {
    setReconLoading(true);
    setReconError('');
    try {
      const results = [];
      for (const merchant of merchants) {
        // eslint-disable-next-line no-await-in-loop
        const txns = await backendTransactionService.getMerchantTransactions(merchant.id);
        const mismatches = (txns || []).filter(isAmountMismatch);
        if (mismatches.length > 0) {
          results.push({ merchant, mismatches });
        }
      }
      setReconByMerchant(results);
      setReconLoaded(true);
    } catch (e) {
      console.error('Error loading reconciliation:', e);
      setReconError(e.message || 'Failed to load reconciliation data');
    } finally {
      setReconLoading(false);
    }
  };

  const escapeCsv = (val) => {
    const s = val === null || val === undefined ? '' : String(val);
    return `"${s.replace(/"/g, '""')}"`;
  };

  const buildReconCsv = (groups) => {
    const headers = [
      'Merchant ID', 'Merchant Name', 'Transaction ID', 'Order ID', 'UTR',
      'Intent Amount', 'Paid Amount', 'Difference', 'Status', 'Payment Method',
      'Customer Name', 'Customer Email', 'Customer Phone', 'Date'
    ];
    const rows = [];
    groups.forEach(({ merchant, mismatches }) => {
      mismatches.forEach((t) => {
        const diff = (parseFloat(t.paidAmount) - parseFloat(t.amount)).toFixed(2);
        rows.push([
          merchant.id,
          merchant.businessName || merchant.username || '',
          t.id,
          t.orderId || '',
          t.utr || '',
          t.amount,
          t.paidAmount,
          diff,
          t.status,
          t.paymentMethod,
          t.customerName || '',
          t.customerEmail || '',
          t.customerPhone || '',
          formatIstDateTime(t.createdAt)
        ].map(escapeCsv).join(','));
      });
    });
    return [headers.map(escapeCsv).join(','), ...rows].join('\r\n');
  };

  const downloadCsv = (csv, filename) => {
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const downloadReconAll = () => {
    if (reconByMerchant.length === 0) return;
    const stamp = new Date().toISOString().slice(0, 10);
    downloadCsv(buildReconCsv(reconByMerchant), `amount_mismatches_all_${stamp}.csv`);
  };

  const downloadReconForMerchant = (group) => {
    const stamp = new Date().toISOString().slice(0, 10);
    downloadCsv(buildReconCsv([group]), `amount_mismatch_merchant_${group.merchant.id}_${stamp}.csv`);
  };

  const getStatusBadge = (status) => {
    const statusClass = status === 'SUCCESS' ? 'status-success' : 
                       status === 'PENDING' ? 'status-pending' : 
                       status === 'FAILED' ? 'status-failed' :
                       status === 'REFUNDED' ? 'status-warning' : 'status-secondary';
    return <span className={`status-badge ${statusClass}`}>{status}</span>;
  };

  if (loading) {
    return <div className="container" role="status" aria-live="polite">Loading...</div>;
  }

  const TABS = [
    { key: 'merchants',    label: '👥 Merchants' },
    { key: 'payouts',      label: '💸 Payouts' },
    { key: 'stats',        label: '📊 Statistics' },
    { key: 'merchantMgmt', label: '🏪 Merchant Management' },
    { key: 'bankApi',      label: '🏦 Bank API config' },
    { key: 'payoutUsers',  label: '👤 Payout Users' },
    { key: 'fees',         label: '💰 Fee Slabs' },
    { key: 'revenue',      label: '📊 Revenue' },
    { key: 'ledger',       label: '💰 Ledger' },
    { key: 'reconciliation', label: '🔎 Amount Mismatch' },
  ];

  return (
    <div className="container">
      <div style={{ marginBottom: '16px' }}>
        <h1>Admin Dashboard</h1>
      </div>

      {/* Tab bar — always visible */}
      <div style={{ display: 'flex', gap: '5px', marginBottom: '24px', flexWrap: 'wrap' }}>
        {TABS.map(tab => {
          const isActive = activeTab === tab.key;
          return (
            <button key={tab.key}
              onClick={() => tab.href ? window.location.href = tab.href : setActiveTab(tab.key)}
              style={{
                padding: '7px 11px', fontSize: '11.5px', fontWeight: '600',
                border: 'none', borderRadius: '6px', cursor: 'pointer',
                background: isActive ? '#034659' : '#00aea8',
                color: '#fff',
                boxShadow: isActive ? 'inset 0 2px 4px rgba(0,0,0,0.3)' : 'none',
                transform: isActive ? 'scale(0.97)' : 'scale(1)',
                transition: 'all 0.1s',
                whiteSpace: 'nowrap'
              }}>
              {tab.label}
            </button>
          );
        })}
      </div>
      
      {activeTab === 'overview' && (
        <div>
          <div className="grid grid-4" style={{ marginBottom: '24px' }}>
            <div className="card">
              <p className="stat-card-label">Total Merchants</p>
              <p style={{ fontSize: '2rem', fontWeight: 'bold', color: '#007a75' }}>{merchantStats.totalMerchants}</p>
            </div>
            <div className="card">
              <p className="stat-card-label">Active Merchants</p>
              <p style={{ fontSize: '2rem', fontWeight: 'bold', color: '#28a745' }}>{merchantStats.activeMerchants}</p>
            </div>
            <div className="card">
              <p className="stat-card-label">Total Transactions</p>
              <p style={{ fontSize: '2rem', fontWeight: 'bold', color: '#007a75' }}>{paymentStats.totalPayments}</p>
            </div>
            <div className="card">
              <p className="stat-card-label">Success Rate</p>
              <p style={{ fontSize: '2rem', fontWeight: 'bold', color: '#007a75' }}>
                {paymentStats.successRate ? paymentStats.successRate.toFixed(1) : 0}%
              </p>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'merchants' && (
        <>
          {/* New API Key Display */}
          {newApiKey && (
            <div className="alert alert-success">
              <h3>New API Key Generated:</h3>
              <code style={{ 
                display: 'block', 
                padding: '10px', 
                backgroundColor: '#f8f9fa',
                borderRadius: '4px',
                marginTop: '10px'
              }}>
                {newApiKey}
              </code>
              <p style={{ marginTop: '10px', fontSize: '14px' }}>
                Please save this API key securely. It won't be shown again.
              </p>
              <button 
                className="btn btn-primary"
                onClick={() => setNewApiKey('')}
              >
                Close
              </button>
            </div>
          )}

          {/* Merchants Table */}
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h2 style={{ margin: 0 }}>All Merchants</h2>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <label style={{ fontSize: '13px', fontWeight: '600' }}>Status:</label>
                <select
                  value={merchantStatusFilter}
                  onChange={e => { setMerchantStatusFilter(e.target.value); setMerchantPage(1); }}
                  style={{ padding: '6px 12px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '13px' }}
                  aria-label="Filter merchants by status"
                >
                  <option value="all">All</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
                <input
                  type="text"
                  value={merchantSearch}
                  onChange={e => { setMerchantSearch(e.target.value); setMerchantPage(1); }}
                  placeholder="Search by ID or name..."
                  style={{ padding: '6px 12px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '13px', width: '200px' }}
                  aria-label="Search merchants by ID or name"
                />
                {(merchantStatusFilter !== 'all' || merchantSearch.trim()) && (
                  <button
                    onClick={() => { setMerchantStatusFilter('all'); setMerchantSearch(''); setMerchantPage(1); }}
                    style={{ padding: '6px 12px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '13px', cursor: 'pointer', background: '#f5f5f5' }}
                  >
                    Clear Filters
                  </button>
                )}
              </div>
            </div>
            <div style={{ overflowX: 'auto' }}>
            <table className="table" role="table" style={{ fontSize: '13px' }}>
              <caption className="sr-only">All Merchants</caption>
              <thead>
                <tr>
                  <th scope="col">ID</th>
                  <th scope="col">Username</th>
                  <th scope="col">Email</th>
                  <th scope="col">Business Name</th>
                  <th scope="col">Domain</th>
                  <th scope="col">Status</th>
                  <th scope="col">Txns</th>
                  <th scope="col">Success</th>
                  <th scope="col">Created</th>
                  <th scope="col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {merchants
                  .filter(m => merchantStatusFilter === 'all' ? true : merchantStatusFilter === 'active' ? m.isActive : !m.isActive)
                  .filter(m => {
                    if (!merchantSearch.trim()) return true;
                    const search = merchantSearch.trim().toLowerCase();
                    return (
                      String(m.id).includes(search) ||
                      (m.businessName && m.businessName.toLowerCase().includes(search)) ||
                      (m.username && m.username.toLowerCase().includes(search))
                    );
                  })
                  .slice((merchantPage - 1) * MERCHANT_PAGE_SIZE, merchantPage * MERCHANT_PAGE_SIZE)
                  .map((merchant) => {
                  const txStats = merchantTxStats[merchant.id] || { totalTransactions: 0, successRate: 0 };
                  return (
                    <tr key={merchant.id}>
                      <td>{merchant.id}</td>
                      <td>{merchant.username}</td>
                      <td style={{ fontSize: '12px' }}>{merchant.email}</td>
                      <td>{merchant.businessName || 'Not Set'}</td>
                      <td style={{ fontSize: '11px', color: merchant.domain ? '#28a745' : '#999', maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={merchant.domain}>
                        {merchant.domain || 'Not configured'}
                      </td>
                      <td>
                        <span className={`status-badge ${merchant.isActive ? 'status-success' : 'status-failed'}`}>
                          {merchant.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>{txStats.totalTransactions}</td>
                      <td>{txStats.successRate}%</td>
                      <td style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>{new Date(merchant.createdAt).toLocaleDateString()}</td>
                      <td>
                        <div style={{ display: 'flex', gap: '4px', flexWrap: 'nowrap' }}>
                          <button
                            className="btn btn-primary"
                            style={{ fontSize: '11px', padding: '4px 8px', whiteSpace: 'nowrap' }}
                            onClick={() => generateApiKey(merchant.id)}
                            aria-label={`Generate API Key for ${merchant.username}`}
                          >
                            API Key
                          </button>
                          <button
                            className={`btn ${merchant.isActive ? 'btn-danger' : 'btn-success'}`}
                            style={{ fontSize: '11px', padding: '4px 8px', whiteSpace: 'nowrap' }}
                            onClick={() => toggleMerchantStatus(merchant.id, merchant.isActive)}
                            aria-label={`${merchant.isActive ? 'Deactivate' : 'Activate'} merchant ${merchant.username}`}
                          >
                            {merchant.isActive ? 'Deactivate' : 'Activate'}
                          </button>
                          <button
                            className="btn btn-info"
                            style={{ fontSize: '11px', padding: '4px 8px', whiteSpace: 'nowrap' }}
                            onClick={() => viewMerchantTransactions(merchant.id)}
                            aria-label={`View transactions for ${merchant.username}`}
                          >
                            Txns
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
            <Pagination
              currentPage={merchantPage}
              totalPages={Math.max(1, Math.ceil(merchants.filter(m => merchantStatusFilter === 'all' ? true : merchantStatusFilter === 'active' ? m.isActive : !m.isActive).filter(m => { if (!merchantSearch.trim()) return true; const s = merchantSearch.trim().toLowerCase(); return String(m.id).includes(s) || (m.businessName && m.businessName.toLowerCase().includes(s)) || (m.username && m.username.toLowerCase().includes(s)); }).length / MERCHANT_PAGE_SIZE))}
              goToPage={setMerchantPage}
              total={merchants.filter(m => merchantStatusFilter === 'all' ? true : merchantStatusFilter === 'active' ? m.isActive : !m.isActive).filter(m => { if (!merchantSearch.trim()) return true; const s = merchantSearch.trim().toLowerCase(); return String(m.id).includes(s) || (m.businessName && m.businessName.toLowerCase().includes(s)) || (m.username && m.username.toLowerCase().includes(s)); }).length}
              pageSize={MERCHANT_PAGE_SIZE}
            />
          </div>
        </>
      )}

      {activeTab === 'payouts' && (
        <AdminPayoutManagement />
      )}

      {activeTab === 'payoutUsers' && (
        <AdminPayoutUserManagement />
      )}

      {activeTab === 'merchantMgmt' && (
        <AdminMerchantManagement />
      )}

      {activeTab === 'bankApi' && (
        <AdminBankApiConfig />
      )}

      {activeTab === 'fees' && (
        <AdminFeeSlabConfig />
      )}

      {activeTab === 'revenue' && (
        <AdminRevenueDashboard />
      )}

      {activeTab === 'ledger' && (
        <ErrorBoundary>
          <Suspense fallback={<div style={{ padding: '40px', textAlign: 'center' }}>Loading Ledger...</div>}>
            <AdminLedgerDashboard />
          </Suspense>
        </ErrorBoundary>
      )}

      {activeTab === 'reconciliation' && (
        <>
          <div className="card" style={{ marginBottom: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
              <div>
                <h2 style={{ margin: 0 }}>Amount Mismatch (Intent vs Paid)</h2>
                <p style={{ margin: '6px 0 0', fontSize: '13px', color: '#666' }}>
                  Disputes only: transactions where the amount the customer actually paid differs
                  from the intent amount are shown here, grouped by merchant. Use the download
                  buttons to share the details.
                </p>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  className="btn btn-primary"
                  onClick={loadReconciliation}
                  disabled={reconLoading}
                  style={{ fontSize: '13px', padding: '8px 16px' }}
                >
                  {reconLoading ? 'Checking…' : (reconLoaded ? '🔄 Re-check' : '🔎 Run Check')}
                </button>
                <button
                  className="btn btn-success"
                  onClick={downloadReconAll}
                  disabled={reconByMerchant.length === 0}
                  style={{ fontSize: '13px', padding: '8px 16px' }}
                  title="Download all merchants' mismatches as CSV"
                >
                  ⬇️ Download All (CSV)
                </button>
              </div>
            </div>
            {reconError && (
              <div className="alert alert-danger" style={{ marginTop: '12px' }}>{reconError}</div>
            )}
          </div>

          {reconLoading ? (
            <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
              Checking all merchants for amount mismatches…
            </div>
          ) : !reconLoaded ? (
            <div className="card" style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              Click "Run Check" to scan all merchants for request/response amount mismatches.
            </div>
          ) : reconByMerchant.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', padding: '40px', color: '#28a745' }}>
              ✅ No amount mismatches found. All requested amounts match the amounts confirmed by the bank.
            </div>
          ) : (
            reconByMerchant.map((group) => (
              <div className="card" key={group.merchant.id} style={{ marginBottom: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px', marginBottom: '10px' }}>
                  <h3 style={{ margin: 0 }}>
                    {group.merchant.businessName || group.merchant.username} (ID: {group.merchant.id})
                    <span style={{ marginLeft: '10px', fontSize: '13px', color: '#dc3545', fontWeight: 600 }}>
                      {group.mismatches.length} mismatch{group.mismatches.length > 1 ? 'es' : ''}
                    </span>
                  </h3>
                  <button
                    className="btn btn-info"
                    onClick={() => downloadReconForMerchant(group)}
                    style={{ fontSize: '12px', padding: '6px 12px' }}
                    title={`Download mismatches for ${group.merchant.businessName || group.merchant.username}`}
                  >
                    ⬇️ Download (CSV)
                  </button>
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <table className="table" style={{ fontSize: '13px' }}>
                    <caption className="sr-only">Amount mismatches for merchant {group.merchant.id}</caption>
                    <thead>
                      <tr>
                        <th scope="col">Transaction ID</th>
                        <th scope="col">UTR</th>
                        <th scope="col">Intent Amt</th>
                        <th scope="col">Paid Amt</th>
                        <th scope="col">Difference</th>
                        <th scope="col">Status</th>
                        <th scope="col">Method</th>
                        <th scope="col">Customer</th>
                        <th scope="col">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.mismatches.map((t) => {
                        const diff = parseFloat(t.paidAmount) - parseFloat(t.amount);
                        return (
                          <tr key={t.id}>
                            <td style={{ fontSize: '12px' }}>{t.id}</td>
                            <td style={{ fontSize: '12px' }}>{t.utr || '-'}</td>
                            <td>{formatAmount(t.amount)}</td>
                            <td>{formatAmount(t.paidAmount)}</td>
                            <td style={{ color: diff < 0 ? '#dc3545' : '#e08600', fontWeight: 600 }}>
                              {diff > 0 ? '+' : ''}{formatAmount(diff)}
                            </td>
                            <td>{getStatusBadge(t.status)}</td>
                            <td>{t.paymentMethod}</td>
                            <td style={{ fontSize: '12px' }}>
                              <div>{t.customerName || 'N/A'}</div>
                              <div style={{ color: '#666' }}>{t.customerEmail || ''}</div>
                              <div style={{ color: '#666' }}>{t.customerPhone || ''}</div>
                            </td>
                            <td style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>{formatIstDateTime(t.createdAt)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ))
          )}
        </>
      )}

      {activeTab === 'stats' && (
        <>
          {/* System Stats */}
          <h2>System Statistics</h2>
          
          <h3>Merchant Stats</h3>
          <div className="grid grid-3">
            <div className="card">
              <p className="stat-card-label">Total Merchants</p>
              <p style={{ fontSize: '2rem', fontWeight: 'bold', color: '#007a75' }}>
                {merchantStats.totalMerchants}
              </p>
            </div>
            <div className="card">
              <p className="stat-card-label">Active Merchants</p>
              <p style={{ fontSize: '2rem', fontWeight: 'bold', color: '#28a745' }}>
                {merchantStats.activeMerchants}
              </p>
            </div>
            <div className="card">
              <p className="stat-card-label">Inactive Merchants</p>
              <p style={{ fontSize: '2rem', fontWeight: 'bold', color: '#dc3545' }}>
                {merchantStats.inactiveMerchants}
              </p>
            </div>
          </div>

          <h3 style={{ marginTop: '20px' }}>Payment Stats</h3>
          <div className="grid grid-3">
            <div className="card">
              <p className="stat-card-label">Total Payments</p>
              <p style={{ fontSize: '2rem', fontWeight: 'bold', color: '#007a75' }}>
                {paymentStats.totalPayments}
              </p>
            </div>
            <div className="card">
              <p className="stat-card-label">Successful Payments</p>
              <p style={{ fontSize: '2rem', fontWeight: 'bold', color: '#28a745' }}>
                {paymentStats.successfulPayments}
              </p>
            </div>
            <div className="card">
              <p className="stat-card-label">Total Amount</p>
              <p style={{ fontSize: '2rem', fontWeight: 'bold', color: '#28a745' }}>
                {formatAmount(paymentStats.totalAmount || 0)}
              </p>
            </div>
          </div>

          <h3 style={{ marginTop: '20px' }}>Payout Stats</h3>
          <div className="grid grid-3">
            <div className="card">
              <p className="stat-card-label">Total Payouts</p>
              <p style={{ fontSize: '2rem', fontWeight: 'bold', color: '#007a75' }}>
                {payoutStats.totalPayouts}
              </p>
            </div>
            <div className="card">
              <p className="stat-card-label">Successful Payouts</p>
              <p style={{ fontSize: '2rem', fontWeight: 'bold', color: '#28a745' }}>
                {payoutStats.successfulPayouts}
              </p>
            </div>
            <div className="card">
              <p className="stat-card-label">Payout Volume</p>
              <p style={{ fontSize: '2rem', fontWeight: 'bold', color: '#28a745' }}>
                {formatAmount(payoutStats.totalVolume || 0)}
              </p>
            </div>
            <div className="card">
              <p className="stat-card-label">Pending Payouts</p>
              <p style={{ fontSize: '2rem', fontWeight: 'bold', color: '#ffc107' }}>
                {payoutStats.pendingPayouts}
              </p>
            </div>
            <div className="card">
              <p className="stat-card-label">Failed Payouts</p>
              <p style={{ fontSize: '2rem', fontWeight: 'bold', color: '#dc3545' }}>
                {payoutStats.failedPayouts}
              </p>
            </div>
            <div className="card">
              <p className="stat-card-label">Payout Success Rate</p>
              <p style={{ fontSize: '2rem', fontWeight: 'bold', color: '#007a75' }}>
                {payoutStats.successRate ? payoutStats.successRate.toFixed(1) : 0}%
              </p>
            </div>
          </div>
        </>
      )}


      {/* Transaction Details Modal */}
      {selectedMerchantTransactions && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '8px',
            width: '90%',
            maxWidth: '1200px',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3>Transaction Details - Merchant {selectedMerchantTransactions.merchantId}</h3>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  className="btn btn-primary"
                  aria-label="Refresh transaction data"
                  onClick={() => {
                    viewMerchantTransactions(selectedMerchantTransactions.merchantId, true);
                  }}
                  style={{ fontSize: '14px', padding: '8px 16px' }}
                >
                  🔄 Refresh 
                </button>
                <button
                  className="btn btn-danger"
                  onClick={() => setSelectedMerchantTransactions(null)}
                >
                  Close
                </button>
              </div>
            </div>
            
            {/* Transaction Stats */}
            <div className="card" style={{ marginBottom: '20px' }}>
              <h3>Transaction Summary</h3>
              {selectedMerchantTransactions.filteredTransactions && 
               selectedMerchantTransactions.filteredTransactions.length !== selectedMerchantTransactions.transactions.length && (
                <div style={{ marginBottom: '10px', padding: '10px', backgroundColor: '#e3f2fd', borderRadius: '4px', fontSize: '14px' }}>
                  <strong>Showing filtered results:</strong> {selectedMerchantTransactions.filteredTransactions.length} of {selectedMerchantTransactions.transactions.length} total transactions
                </div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
                <div>
                  <strong>Total Transactions:</strong> {
                    selectedMerchantTransactions.filteredTransactions 
                      ? calculateFilteredStats(selectedMerchantTransactions.filteredTransactions).totalTransactions
                      : selectedMerchantTransactions.stats.totalTransactions
                  }
                </div>
                <div>
                  <strong>Successful:</strong> {
                    selectedMerchantTransactions.filteredTransactions 
                      ? calculateFilteredStats(selectedMerchantTransactions.filteredTransactions).successfulTransactions
                      : selectedMerchantTransactions.stats.successfulTransactions
                  }
                </div>
                <div>
                  <strong>Success Rate:</strong> {
                    selectedMerchantTransactions.filteredTransactions 
                      ? calculateFilteredStats(selectedMerchantTransactions.filteredTransactions).successRate
                      : selectedMerchantTransactions.stats.successRate
                  }%
                </div>
                <div>
                  <strong>Total Amount:</strong> ₹{
                    selectedMerchantTransactions.filteredTransactions 
                      ? calculateFilteredStats(selectedMerchantTransactions.filteredTransactions).totalAmount.toLocaleString()
                      : selectedMerchantTransactions.stats.totalAmount.toLocaleString()
                  }
                </div>
                <div>
                  <strong>Total Fees:</strong> ₹{
                    selectedMerchantTransactions.filteredTransactions 
                      ? calculateFilteredStats(selectedMerchantTransactions.filteredTransactions).totalFees.toLocaleString()
                      : selectedMerchantTransactions.stats.totalFees.toLocaleString()
                  }
                </div>
                <div>
                  <strong>Failed:</strong> {
                    selectedMerchantTransactions.filteredTransactions 
                      ? calculateFilteredStats(selectedMerchantTransactions.filteredTransactions).failedTransactions
                      : selectedMerchantTransactions.stats.failedTransactions
                  }
                </div>
              </div>
            </div>

            {/* Transaction Filters */}
            <div className="card" style={{ marginBottom: '20px' }}>
              <h3>Filter Transactions</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px', alignItems: 'end' }}>
                <div>
                  <label>Time Period:</label>
                  <select
                    value={transactionFilters.timePeriod}
                    onChange={(e) => setTransactionFilters(prev => ({ ...prev, timePeriod: e.target.value }))}
                    className="form-control"
                  >
                    <option value="">All Time</option>
                    <option value="today">Today</option>
                    <option value="week">This Week</option>
                    <option value="month">This Month</option>
                    <option value="year">This Year</option>
                    <option value="lifetime">Lifetime</option>
                  </select>
                </div>
                <div>
                  <label>Status:</label>
                  <select
                    value={transactionFilters.status}
                    onChange={(e) => setTransactionFilters(prev => ({ ...prev, status: e.target.value }))}
                    className="form-control"
                  >
                    <option value="">All Status</option>
                    <option value="SUCCESS">Success</option>
                    <option value="PENDING">Pending</option>
                    <option value="FAILED">Failed</option>
                    <option value="REFUNDED">Refunded</option>
                  </select>
                </div>
                <div>
                  <label>Payment Method:</label>
                  <select
                    value={transactionFilters.paymentMethod}
                    onChange={(e) => setTransactionFilters(prev => ({ ...prev, paymentMethod: e.target.value }))}
                    className="form-control"
                  >
                    <option value="">All Methods</option>
                    <option value="UPI">UPI</option>
                    <option value="CARD">Card</option>
                    <option value="NET_BANKING">Net Banking</option>
                    <option value="WALLET">Wallet</option>
                  </select>
                </div>
                <div>
                  <label>From Date:</label>
                  <input
                    type="date"
                    value={transactionFilters.dateFrom}
                    onChange={(e) => setTransactionFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
                    className="form-control"
                  />
                </div>
                <div>
                  <label>To Date:</label>
                  <input
                    type="date"
                    value={transactionFilters.dateTo}
                    onChange={(e) => setTransactionFilters(prev => ({ ...prev, dateTo: e.target.value }))}
                    className="form-control"
                  />
                </div>
                <div>
                  <label>Min Amount:</label>
                  <input
                    type="number"
                    value={transactionFilters.minAmount}
                    onChange={(e) => setTransactionFilters(prev => ({ ...prev, minAmount: e.target.value }))}
                    placeholder="0"
                    className="form-control"
                    min="0"
                    step="0.01"
                  />
                </div>
                <div>
                  <label>Max Amount:</label>
                  <input
                    type="number"
                    value={transactionFilters.maxAmount}
                    onChange={(e) => setTransactionFilters(prev => ({ ...prev, maxAmount: e.target.value }))}
                    placeholder="No limit"
                    className="form-control"
                    min="0"
                    step="0.01"
                  />
                </div>
                <div>
                  <label>Search:</label>
                  <input
                    type="text"
                    value={transactionFilters.searchTerm}
                    onChange={(e) => setTransactionFilters(prev => ({ ...prev, searchTerm: e.target.value }))}
                    placeholder="Order ID, Email, etc."
                    className="form-control"
                  />
                </div>
                <div>
                  <button
                    className="btn btn-secondary"
                    onClick={clearTransactionFilters}
                  >
                    Clear Filters
                  </button>
                </div>
                {/* <div>
                  <button
                    className="btn btn-success"
                    onClick={() => {
                      if (selectedMerchantTransactions) {
                        console.log('🔄 Generating new sample transactions...');
                        const newTransactions = transactionService.generateMockTransactions(selectedMerchantTransactions.merchantId, 25);
                        localStorage.setItem(`merchant_transactions_${selectedMerchantTransactions.merchantId}`, JSON.stringify(newTransactions));
                        
                        // Refresh the transaction view
                        const stats = transactionService.getMerchantTransactionStats(selectedMerchantTransactions.merchantId);
                        setSelectedMerchantTransactions({
                          merchantId: selectedMerchantTransactions.merchantId,
                          transactions: newTransactions,
                          stats,
                          filteredTransactions: newTransactions
                        });
                        
                        // Clear filters to show all new transactions
                        setTransactionFilters({
                          timePeriod: '',
                          status: '',
                          paymentMethod: '',
                          dateFrom: '',
                          dateTo: '',
                          minAmount: '',
                          maxAmount: '',
                          searchTerm: ''
                        });
                        
                        console.log('✅ Generated', newTransactions.length, 'new transactions');
                      }
                    }}
                  >
                    Generate Sample Data
                  </button>
                </div> */}
                {/* <div>
                  <button
                    className="btn btn-info"
                    onClick={() => {
                      console.log('🔍 Debug Info:');
                      console.log('Selected Merchant Transactions:', selectedMerchantTransactions);
                      console.log('Current Filters:', transactionFilters);
                      if (selectedMerchantTransactions) {
                        console.log('Sample Transaction:', selectedMerchantTransactions.transactions[0]);
                        console.log('Transaction Dates:', selectedMerchantTransactions.transactions.slice(0, 3).map(t => ({
                          id: t.id,
                          createdAt: t.createdAt,
                          date: new Date(t.createdAt)
                        })));
                      }
                    }}
                  >
                    Debug
                  </button>
                </div> */}
              </div>
            </div>

            {/* Transactions Table */}
            <div className="card">
              <h3>Transactions ({selectedMerchantTransactions.filteredTransactions.length})</h3>
              {selectedMerchantTransactions.filteredTransactions.length > 0 ? (
                <div style={{ overflowX: 'auto' }}>
                  <table className="table">
                    <caption className="sr-only">Merchant Transactions</caption>
                    <thead>
                      <tr>
                        <th scope="col">Transaction ID</th>
                        <th scope="col">Order ID</th>
                        <th scope="col">Amount</th>
                        <th scope="col">Status</th>
                        <th scope="col">Payment Method</th>
                        <th scope="col">Customer</th>
                        <th scope="col">Date</th>
                        <th scope="col">Gateway ID</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedMerchantTransactions.filteredTransactions
                        .slice((adminTxPage - 1) * ADMIN_TX_PAGE_SIZE, adminTxPage * ADMIN_TX_PAGE_SIZE)
                        .map((transaction) => (
                        <tr key={transaction.id}>
                          <td style={{ fontSize: '12px' }}>{transaction.id}</td>
                          <td>{transaction.orderId}</td>
                          <td>₹{transaction.amount.toLocaleString()}</td>
                          <td>
                            <span className={`status-badge ${
                              transaction.status === 'SUCCESS' ? 'status-success' :
                              transaction.status === 'PENDING' ? 'status-pending' :
                              transaction.status === 'FAILED' ? 'status-failed' : 'status-secondary'
                            }`}>
                              {transaction.status}
                            </span>
                          </td>
                          <td>{transaction.paymentMethod}</td>
                          <td>
                            <div style={{ fontSize: '12px' }}>
                              <div><strong>Name:</strong> {transaction.customerName || 'Not provided'}</div>
                              <div><strong>Email:</strong> {transaction.customerEmail || 'Not provided'}</div>
                              <div><strong>Phone:</strong> {transaction.customerPhone || 'Not provided'}</div>
                            </div>
                          </td>
                          <td style={{ fontSize: '12px' }}>
                            {new Date(transaction.createdAt).toLocaleString()}
                          </td>
                          <td style={{ fontSize: '12px' }}>{transaction.gatewayTransactionId}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <Pagination
                    currentPage={adminTxPage}
                    totalPages={Math.max(1, Math.ceil(selectedMerchantTransactions.filteredTransactions.length / ADMIN_TX_PAGE_SIZE))}
                    goToPage={setAdminTxPage}
                    total={selectedMerchantTransactions.filteredTransactions.length}
                    pageSize={ADMIN_TX_PAGE_SIZE}
                  />
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <p style={{ fontSize: '16px', color: '#666', marginBottom: '20px' }}>
                    No transactions found matching the current filters.
                  </p>
                  {selectedMerchantTransactions.transactions.length === 0 ? (
                    <div>
                      <p style={{ color: '#767676', marginBottom: '15px' }}>
                        This merchant has no transactions in the database yet.
                      </p>
                    </div>
                  ) : (
                    <div>
                      <p style={{ color: '#767676', marginBottom: '15px' }}>
                        Try adjusting your filters or click "Clear Filters" to see all {selectedMerchantTransactions.transactions.length} transactions.
                      </p>
                      <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                        <button
                          className="btn btn-secondary"
                          onClick={clearTransactionFilters}
                        >
                          Clear All Filters
                        </button>
                        <button
                          className="btn btn-info"
                          onClick={() => {
                            console.log('🔍 Debug Info:');
                            console.log('Selected Merchant Transactions:', selectedMerchantTransactions);
                            console.log('Current Filters:', transactionFilters);
                            console.log('Using Backend Data:', useBackendData);
                          }}
                        >
                          Debug Info
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const SystemKeysPanel = () => {
  const [message, setMessage] = useState('');
  const [currentCode, setCurrentCode] = useState('');
  const [newCode, setNewCode] = useState('');
  const [confirmCode, setConfirmCode] = useState('');

  const handleChangeMyCode = async () => {
    if (newCode !== confirmCode) {
      setMessage('New codes do not match');
      return;
    }
    if (!newCode || newCode.length < 4) {
      setMessage('New access code must be at least 4 characters');
      return;
    }
    try {
      const response = await axios.put('/api/admin/config/my-access-code', {
        currentCode: currentCode,
        newCode: newCode
      });
      if (response.data.success) {
        setMessage(response.data.data || 'Access code updated successfully');
        setCurrentCode('');
        setNewCode('');
        setConfirmCode('');
        setTimeout(() => setMessage(''), 5000);
      }
    } catch (error) {
      setMessage(error.response?.data?.error || 'Failed to update access code');
    }
  };

  return (
    <div>
      {message && (
        <div style={{
          padding: '10px 15px', marginBottom: '15px', borderRadius: '5px',
          background: message.includes('Failed') || message.includes('do not match') || message.includes('incorrect') ? '#f8d7da' : '#d4edda',
          color: message.includes('Failed') || message.includes('do not match') || message.includes('incorrect') ? '#721c24' : '#155724'
        }}>
          {message}
        </div>
      )}

      {/* Change My Access Code */}
      <div className="card" style={{ padding: '20px', marginBottom: '20px' }}>
        <h3>🔐 Change My Admin Access Code</h3>
        <p style={{ color: '#666' }}>Update your personal admin login access code. This is stored securely (encrypted) and cannot be viewed.</p>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div>
            <label style={{ display: 'block', fontSize: '14px', marginBottom: '4px' }}>Current Code</label>
            <input type="password" value={currentCode} onChange={(e) => setCurrentCode(e.target.value)}
              placeholder="Current access code" style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '14px', marginBottom: '4px' }}>New Code</label>
            <input type="password" value={newCode} onChange={(e) => setNewCode(e.target.value)}
              placeholder="New access code" style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '14px', marginBottom: '4px' }}>Confirm New Code</label>
            <input type="password" value={confirmCode} onChange={(e) => setConfirmCode(e.target.value)}
              placeholder="Confirm new code" style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }} />
          </div>
          <button
            style={{ padding: '8px 16px', background: (!currentCode || !newCode || !confirmCode) ? '#ccc' : '#007bff', color: '#fff', border: 'none', borderRadius: '4px', cursor: (!currentCode || !newCode || !confirmCode) ? 'not-allowed' : 'pointer' }}
            onClick={handleChangeMyCode}
            disabled={!currentCode || !newCode || !confirmCode}>
            Update My Code
          </button>
        </div>
      </div>

      <div className="card" style={{ padding: '20px' }}>
        <h3>ℹ️ Security Information</h3>
        <p style={{ color: '#666' }}>All keys are stored encrypted (BCrypt) in the database. They cannot be viewed, only changed.</p>
        <ul style={{ color: '#666', fontSize: '14px' }}>
          <li><strong>Master Key</strong> — Required during admin signup. Configured in server environment.</li>
          <li><strong>Admin Access Code</strong> — Per-user, set during signup, changeable above.</li>
          <li><strong>Payout Access Key</strong> — Per-user, set during payout user creation. Changeable by the payout user.</li>
        </ul>
      </div>
    </div>
  );
};

export default AdminDashboard;